import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Clock, Users, ArrowRight, Info } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TripsPage = () => {
  const { t, language, getLocalizedField } = useLanguage();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const response = await axios.get(`${API}/trips`);
        setTrips(response.data);
      } catch (error) {
        console.error('Error fetching trips:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-28 sm:pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="skeleton h-12 w-64 mb-8 rounded" />
          <div className="grid gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden card-shadow flex flex-col lg:flex-row">
                <div className="skeleton h-64 lg:h-auto lg:w-96" />
                <div className="p-8 flex-1 space-y-4">
                  <div className="skeleton h-8 w-3/4 rounded" />
                  <div className="skeleton h-4 w-full rounded" />
                  <div className="skeleton h-4 w-2/3 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 bg-[#000000]" data-testid="trips-page">
      {/* Header */}
      <div className="bg-[#000000] pt-28 sm:pt-36 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-4">
            {t('trips.title')}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Que vous soyez débutant ou pêcheur aguerri, nous vous proposons des sorties adaptées à vos envies
          </p>
        </div>
      </div>

      {/* Trips List */}
      <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Alert className="mb-8 border-[#b7ede0]/30 bg-[#b7ede0]/5">
          <Info className="h-5 w-5 text-[#b7ede0]" />
          <AlertDescription className="text-[#4B5563]">
            {t('trips.important')}
          </AlertDescription>
        </Alert>

        <div className="grid gap-8">
          {trips.map((trip, index) => (
            <div 
              key={trip.id} 
              className={`bg-white rounded-2xl overflow-hidden card-shadow hover-lift flex flex-col lg:flex-row animate-fade-up stagger-${Math.min(index + 1, 5)}`}
              data-testid={`trip-item-${index}`}
            >
              {/* Image */}
              <div className="relative lg:w-96 flex-shrink-0">
                <img
                  src={trip.image_url}
                  alt={getLocalizedField(trip, 'name')}
                  className="w-full h-64 lg:h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-[#000000]">
                    <Clock size={14} className="inline mr-1" />
                    {trip.duration_options && trip.duration_options.length > 0
                      ? trip.duration_options.map(o => o.label).join(' / ')
                      : `${trip.duration_hours}h`}
                  </span>
                  <span className="px-3 py-1 bg-[#b7ede0] text-[#000000] rounded-full text-sm font-medium">
                    <Users size={14} className="inline mr-1" />
                    Max {trip.max_participants}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-8 flex flex-col">
                <h2 className="text-2xl font-bold text-[#000000] font-['Playfair_Display'] mb-4">
                  {getLocalizedField(trip, 'name')}
                </h2>
                <p className="text-[#4B5563] mb-6 flex-1">
                  {getLocalizedField(trip, 'description')}
                </p>

                {/* Time Slots */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-[#000000] mb-2">{t('trips.availableSlots')}:</p>
                  <div className="flex flex-wrap gap-2">
                    {trip.time_slots.map((slot, i) => (
                      <span key={i} className="time-slot-badge">
                        {slot.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Pricing & CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-[#E2E8F0]">
                  <div className="space-y-1">
                    {trip.duration_options && trip.duration_options.length > 0 ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-[#000000]">
                            {language === 'fr' ? 'À partir de ' : language === 'it' ? 'Da ' : 'From '}{Math.min(...trip.duration_options.map(o => o.price_per_person))}€
                          </span>
                          <span className="text-[#4B5563]">{t('trips.perPerson')}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-[#4B5563]">
                          {trip.duration_options.map((opt, oi) => (
                            <span key={oi}>{opt.label}: <span className="font-semibold text-[#000000]">{opt.price_per_person}€</span>/pers.</span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-[#000000]">{trip.price_per_person}€</span>
                          <span className="text-[#4B5563]">{t('trips.perPerson')}</span>
                        </div>
                        <p className="text-sm text-[#4B5563]">
                          {t('trips.privatization')}: <span className="font-semibold text-[#000000]">{trip.privatization_price}€</span>
                        </p>
                      </>
                    )}
                  </div>
                  <Link to={`/trips/${trip.id}`}>
                    <Button 
                      className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full px-8 gap-2"
                      data-testid={`book-trip-${index}-btn`}
                    >
                      {t('trips.book')}
                      <ArrowRight size={18} />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default TripsPage;
